import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowRightIcon,
	BoltIcon,
	CheckCircleIcon,
	CurrencyDollarIcon,
	ShieldCheckIcon,
	SunIcon,
	HomeModernIcon,
	BuildingStorefrontIcon,
	WrenchScrewdriverIcon,
	Battery100Icon,
	ArrowPathRoundedSquareIcon,
	WrenchIcon,
} from '@heroicons/react/24/outline';
import HEADER from '../../assets/images/stock/panels.jpg';
import GABE_SELFIE from '../../assets/images/2026/gabe-selfie.png';
import GROUND_MOUNT from '../../assets/images/2026/IMG_5981.png';
import Testimonials from '../Testimonials';
import ServiceAreaMap from '../Services/ServiceAreaMap';
import seo from '../SEO/data';
import { serviceAreas } from '../Services/data';

const services = [
	{
		name: 'Residential Solar',
		description:
			'Custom rooftop systems designed around your roof, your bill, and your timeline.',
		icon: HomeModernIcon,
		href: '/services/residential-solar',
	},
	{
		name: 'Commercial Solar',
		description:
			'Lower operating costs and meet sustainability goals with a system scoped for your operation.',
		icon: BuildingStorefrontIcon,
		href: '/services/commercial-solar',
	},
	{
		name: 'Roofing + Solar',
		description:
			'Coordinated roof replacement and solar install. One contact, one warranty, one timeline.',
		icon: WrenchScrewdriverIcon,
		href: '/services/roofing-installation',
	},
	{
		name: 'Remove & Reinstall',
		description:
			'Professional removal and reinstall of your existing solar panels. Perfect for roof repairs, replacements, or if you’re moving.',
		icon: ArrowPathRoundedSquareIcon,
		href: '/services/remove-reinstall',
	},
	{
		name: 'Maintenance',
		description: 'Regular upkeep to ensure your system performs at its best.',
		icon: WrenchIcon,
		href: '/services/maintenance',
	},
	{
		name: 'Storage & Backup',
		description:
			'Battery backup paired with your array. Power through outages and stretch every kWh.',
		icon: Battery100Icon,
		href: '/services/storage-backup',
	},
];

const benefits = [
	{
		title: 'Real savings, modeled',
		body: 'Most Maryland homeowners cut their electric bill 70–100%. I show you the math before you sign anything.',
		icon: CurrencyDollarIcon,
	},
	{
		title: 'One person, start to finish',
		body: 'No call centers. No handoffs. You work with me from first quote through final inspection.',
		icon: CheckCircleIcon,
	},
	{
		title: 'Equipment that lasts',
		body: 'Tier-1 panels, leading inverters, and workmanship warranties that actually mean something.',
		icon: ShieldCheckIcon,
	},
	{
		title: 'End-to-end service',
		body: 'Site survey, design, permitting, install, inspection, and monitoring — all handled.',
		icon: BoltIcon,
	},
];

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

function Hero() {
	return (
		<section className='relative isolate overflow-hidden bg-ink-900'>
			<img
				src={HEADER}
				alt=''
				className='absolute inset-0 -z-10 h-full w-full object-cover opacity-35'
			/>
			<div className='absolute inset-0 -z-10 bg-gradient-to-br from-ink-900/85 via-ink-900/70 to-ink-800/55' />
			<div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40'>
				<div className='grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center'>
					<div className='lg:col-span-7'>
						<Eyebrow dark>
							Maryland · Pennsylvania · Virginia · DC
						</Eyebrow>
						<h1 className='font-display mt-6 text-5xl font-medium tracking-tight text-white sm:text-6xl lg:text-7xl leading-[1.05]'>
							Solar that
							<br />
							<span className='italic text-primary-400'>actually</span> pays
							off.
						</h1>
						<p className='mt-8 max-w-xl text-lg leading-8 text-white/75'>
							Work directly with a solar installer, foreman, and project
							manager — not a salesperson. From your first quote to final
							inspection, going solar made simple.
						</p>
						<div className='mt-10 flex flex-wrap items-center gap-x-6 gap-y-4'>
							<Link
								to='/contact'
								className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 transition-all'
							>
								Schedule a free solar assessment
								<ArrowRightIcon className='h-5 w-5' />
							</Link>
							<Link
								to='/services'
								className='inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white transition-colors'
							>
								See what we do
								<ArrowRightIcon className='h-4 w-4' />
							</Link>
						</div>
						<div className='mt-14 grid grid-cols-3 gap-8 max-w-md border-t border-white/10 pt-8'>
							<div>
								<div className='font-display text-3xl text-white'>500+</div>
								<div className='text-xs text-white/60 mt-1'>Homes powered</div>
							</div>
							<div>
								<div className='font-display text-3xl text-white'>5.0★</div>
								<div className='text-xs text-white/60 mt-1'>Google rating</div>
							</div>
							<div>
								<div className='font-display text-3xl text-white'>30yr</div>
								<div className='text-xs text-white/60 mt-1'>Warranty</div>
							</div>
						</div>
					</div>
					<div className='hidden lg:col-span-5 lg:flex lg:justify-end lg:items-center'>
						<div className='relative'>
							<div className='absolute -inset-8 rounded-full bg-primary-500/20 blur-3xl' />
							<img
								src={GABE_SELFIE}
								alt='Gabe in front of a ground-mounted solar array'
								className='relative h-[30rem] w-[26rem] rounded-3xl object-cover object-top shadow-2xl shadow-ink-900/30 ring-1 ring-white/10'
							/>
							<div className='absolute bottom-4 left-4 right-4 rounded-2xl bg-ink-900/70 px-5 py-3 backdrop-blur-sm ring-1 ring-white/10'>
								<p className='text-sm font-semibold text-white'>
									Gabriel Shabtai
								</p>
								<p className='text-xs text-white/70'>
									Former installer · foreman · electrician · PM
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

// "Why homeowners work with me" — the credentials no other consultant can claim.
// Straight, simple, powerful (idea #8).
function TrustStrip() {
	const credentials = [
		'Former solar installer',
		'Former foreman',
		'Former electrician',
		'Project manager',
		'U.S. military veteran',
		'Hundreds of projects managed',
	];
	return (
		<section className='border-y border-ink-100 bg-white py-12'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
					<p className='font-display text-lg font-medium text-ink-900 lg:max-w-xs'>
						Why homeowners work with me
					</p>
					<div className='flex flex-wrap gap-x-6 gap-y-3'>
						{credentials.map((c) => (
							<span
								key={c}
								className='inline-flex items-center gap-2 text-sm font-medium text-ink-700'
							>
								<CheckCircleIcon className='h-5 w-5 flex-none text-primary-500' />
								{c}
							</span>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

function ServicesGrid() {
	return (
		<section className='bg-surface py-24 sm:py-32'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='mx-auto max-w-2xl text-center'>
					<div className='flex justify-center'>
						<Eyebrow>What we do</Eyebrow>
					</div>
					<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl'>
						Solar, done <span className='italic text-primary-600'>right</span>.
					</h2>
					<p className='mt-6 text-base leading-7 text-ink-500'>
						Every project is built around your roof, your bill, and your goals.
						Never a one-size-fits-all package.
					</p>
				</div>
				<div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 lg:max-w-none lg:grid-cols-2 xl:grid-cols-4'>
					{services.map((s) => (
						<Link
							key={s.name}
							to={s.href}
							className='group sg-card sg-card-interactive p-8'
						>
							<div className='sg-badge'>
								<s.icon
									className='h-5 w-5'
									aria-hidden='true'
								/>
							</div>
							<h3 className='font-display mt-6 text-xl font-medium text-ink-900'>
								{s.name}
							</h3>
							<p className='mt-3 text-sm leading-6 text-ink-500 flex-1'>
								{s.description}
							</p>
							<span className='mt-6 inline-flex items-center text-sm font-semibold text-primary-600 group-hover:gap-2 gap-1 transition-all'>
								Learn more <ArrowRightIcon className='h-4 w-4' />
							</span>
						</Link>
					))}
				</div>
			</div>
		</section>
	);
}

function BenefitsBlock() {
	return (
		<section className='bg-white py-24 sm:py-32 border-y border-ink-100'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='mx-auto max-w-3xl lg:mx-0'>
					<Eyebrow>Why work with Gabe</Eyebrow>
					<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl leading-tight'>
						No pressure. No surprises. Just solar that{' '}
						<span className='italic text-primary-600'>works</span>.
					</h2>
				</div>
				<div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-4'>
					{benefits.map((b) => (
						<div
							key={b.title}
							className='relative'
						>
							<div className='sg-badge'>
								<b.icon
									className='h-5 w-5'
									aria-hidden='true'
								/>
							</div>
							<h3 className='font-display mt-6 text-xl font-medium text-ink-900'>
								{b.title}
							</h3>
							<p className='mt-2 text-sm leading-6 text-ink-500'>{b.body}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function ServiceAreasPreview() {
	// Shared hover state so the cards and the map highlight together — hovering
	// a card lights its state on the map, and vice versa.
	const [activeArea, setActiveArea] = useState(null);

	return (
		<section className='bg-surface py-24 sm:py-32'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
					<div className='max-w-2xl'>
						<Eyebrow>Where we work</Eyebrow>
						<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl'>
							Area-specific solar guidance.
						</h2>
						<p className='mt-6 text-base leading-7 text-ink-500'>
							Utility rules, permitting, roof conditions, and incentive
							assumptions change by location. Start with the page for your area.
						</p>
					</div>
					<Link
						to='/service-areas'
						className='inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700'
					>
						All service areas
						<ArrowRightIcon className='h-4 w-4' />
					</Link>
				</div>
				<div className='mt-12 grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-12'>
					<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
						{serviceAreas.map((area) => {
							const isActive = activeArea === area.id;
							return (
								<Link
									key={area.id}
									to={`/service-areas/${area.id}`}
									onMouseEnter={() => setActiveArea(area.id)}
									onMouseLeave={() => setActiveArea(null)}
									onFocus={() => setActiveArea(area.id)}
									onBlur={() => setActiveArea(null)}
									className={`group sg-card sg-card-interactive p-6 ${
										isActive ? 'border-primary-500' : ''
									}`}
								>
									<h3 className='font-display text-xl font-medium text-ink-900'>
										{area.name}
									</h3>
									<p className='mt-3 text-sm leading-6 text-ink-500'>
										{area.summary}
									</p>
								</Link>
							);
						})}
					</div>
					<div className='order-first lg:order-last'>
						<ServiceAreaMap
							active={activeArea}
							onActiveChange={setActiveArea}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}

function ProcessBanner() {
	const steps = [
		{
			n: '01',
			title: 'Free site assessment',
			body: `Bring your latest electric bill. I'll model your savings and design options.`,
		},
		{
			n: '02',
			title: 'Custom proposal',
			body: 'A real design for your roof — equipment, financing, timeline, payback. Nothing inflated.',
		},
		{
			n: '03',
			title: 'Permits & install',
			body: `I handle utility paperwork, permits, and coordinate the install crew. You don't lift a finger.`,
		},
		{
			n: '04',
			title: 'Power on & support',
			body: 'System turn-on, monitoring setup, and a person to call when you have questions — for years.',
		},
	];
	return (
		<section className='relative bg-ink-900 py-24 sm:py-32 overflow-hidden'>
			<div
				className='absolute inset-0 opacity-20'
				style={{
					backgroundImage:
						'radial-gradient(circle at 15% 15%, #3CB371 0%, transparent 45%), radial-gradient(circle at 85% 85%, #2A7E4F 0%, transparent 55%)',
				}}
			/>
			<div className='relative mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='max-w-2xl'>
					<Eyebrow dark>The process</Eyebrow>
					<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-white sm:text-5xl leading-tight'>
						Four steps from quote to{' '}
						<span className='italic text-primary-400'>power on</span>.
					</h2>
				</div>
				<div className='mt-16 grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4 bg-white/5 ring-1 ring-white/10 rounded-2xl overflow-hidden'>
					{steps.map((s) => (
						<div
							key={s.n}
							className='bg-ink-900 p-8 hover:bg-ink-800 transition-colors'
						>
							<div className='font-display text-4xl text-primary-400'>
								{s.n}
							</div>
							<h3 className='font-display mt-5 text-lg font-medium text-white'>
								{s.title}
							</h3>
							<p className='mt-2 text-sm leading-6 text-white/65'>{s.body}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

// "My Own Solar Journey" — Gabe owns acreage and runs his own ground-mounted
// array. Practicing what he preaches is credibility you can't fake (idea #7).
function SolarJourney() {
	return (
		<section className='bg-white py-24 sm:py-32 border-t border-ink-100'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center'>
					<div className='lg:col-span-6'>
						<div className='relative mx-auto max-w-lg'>
							<div className='absolute -inset-6 rounded-3xl bg-primary-100/70 blur-2xl' />
							<img
								src={GROUND_MOUNT}
								alt='Gabe’s own ground-mounted solar array on his Maryland property'
								className='relative aspect-[4/3] w-full rounded-3xl object-cover shadow-xl shadow-ink-900/10 ring-1 ring-ink-100'
							/>
						</div>
					</div>
					<div className='lg:col-span-6'>
						<Eyebrow>My own solar journey</Eyebrow>
						<h2 className='font-display mt-4 text-4xl font-medium tracking-tight text-ink-900 sm:text-5xl leading-tight'>
							I practice what I{' '}
							<span className='italic text-primary-600'>preach</span>.
						</h2>
						<div className='mt-8 space-y-5 text-base leading-7 text-ink-500'>
							<p>
								I own acreage in Maryland and I&apos;m building my forever home
								here — with my own solar and battery setup powering it.
							</p>
							<p>
								When I tell you what I&apos;d install, it&apos;s not a sales
								script. It&apos;s the same decision-making I use on my own roof
								and my own ground mount: the right equipment, sized correctly,
								built to last 25+ years.
							</p>
						</div>
						<div className='mt-10'>
							<Link
								to='/about'
								className='inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700'
							>
								More about how I work
								<ArrowRightIcon className='h-4 w-4' />
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

function CtaBanner() {
	return (
		<section className='bg-surface'>
			<div className='mx-auto max-w-7xl px-6 py-24 lg:px-8'>
				<div className='relative isolate overflow-hidden rounded-3xl bg-ink-900 px-8 py-16 sm:px-16 sm:py-20'>
					<div
						className='absolute inset-0 -z-10 opacity-30'
						style={{
							backgroundImage:
								'radial-gradient(ellipse at top right, #3CB371 0%, transparent 55%)',
						}}
					/>
					<div className='mx-auto max-w-2xl text-center'>
						<SunIcon className='mx-auto h-10 w-10 text-primary-400' />
						<h2 className='font-display mt-6 text-3xl font-medium tracking-tight text-white sm:text-4xl leading-tight'>
							Already have a quote? Get a{' '}
							<span className='italic text-primary-400'>second opinion</span>.
						</h2>
						<p className='mt-4 text-base leading-7 text-white/75'>
							Send me your existing proposal or your latest electric bill and
							I&apos;ll give you a straight read — even if you don&apos;t choose
							me.
						</p>
						<div className='mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4'>
							<Link
								to='/contact'
								className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
							>
								Compare my solar proposal
								<ArrowRightIcon className='h-4 w-4' />
							</Link>
							<Link
								to='/ask-gabe'
								className='text-sm font-semibold text-white/85 hover:text-white'
							>
								or ask Gabe a question
							</Link>
							<a
								href={`tel:${seo.company.phone}`}
								className='text-sm font-semibold text-white/85 hover:text-white'
							>
								or call {seo.company.phone}
							</a>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

export default function Landing() {
	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<Hero />
			<TrustStrip />
			<ServicesGrid />
			<BenefitsBlock />
			<ServiceAreasPreview />
			<Testimonials />
			<SolarJourney />
			<ProcessBanner />
			<CtaBanner />
		</div>
	);
}
